import {
  EventEmitter,
  Directive,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Input,
  Output,
  ElementRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { delay } from 'rxjs/operators';

@Directive({ selector: '[observeVisibility]' })
export class ObserveVisibilityDirective implements OnDestroy, OnInit, AfterViewInit {
  @Input() debounceTime = 0;
  @Input() threshold = 1;
  @Input() removeObserver = false;
  @Output() visible = new EventEmitter<HTMLElement>();

  private observer: IntersectionObserver | undefined;
  private subject$ = new Subject<{
    entry: IntersectionObserverEntry;
    observer: IntersectionObserver;
  }>();

  constructor(private element: ElementRef) {}

  ngOnInit() {
    this.createObserver();
  }

  ngAfterViewInit() {
    this.startObservingElements();
  }

  ngOnDestroy() {
    this.destroyObserver();
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }

    this.subject$.next({ entry: null, observer: null });
    this.subject$.complete();
  }

  private createObserver() {
    const options = {
      rootMargin: '0px',
      threshold: this.threshold,
    };

    const isIntersecting = (entry: IntersectionObserverEntry) =>
        entry.isIntersecting || entry.intersectionRatio > 0;

    this.observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (isIntersecting(entry)) {
          this.subject$.next({ entry, observer });
        }
      });
    }, options);
  }

  private startObservingElements() {
    if (!this.observer) {
      return;
    }
    const element = this.element.nativeElement;
    this.observer.observe(this.element.nativeElement);

    this.subject$
        .pipe(delay(this.debounceTime))
        .subscribe(({ entry, observer }) => {
          if (entry) {
            if (entry.isIntersecting) {
              this.visible.emit(element);
              if (this.removeObserver) {
                observer.unobserve(element);
              }
            }
        }
        });
  }
}
